import React, { useState, useRef } from 'react';
import { FaPenNib, FaImage, FaMicrophone, FaMusic, FaVideo, FaArrowRight, FaPlay, FaPause } from 'react-icons/fa';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';

const Features: React.FC = () => {
  const headerRef = useScrollAnimation();
  const featuresRef = useScrollAnimation();
  const bottomFeaturesRef = useScrollAnimation();
  const showcaseRef = useScrollAnimation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAudioLoading, setIsAudioLoading] = useState(true);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const isMobile = useIsMobile();

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error("播放失败:", error);
              setIsPlaying(false);
            });
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleAudioPlay = () => {
    if (audioRef.current) {
      if (isAudioPlaying) {
        audioRef.current.pause();
      } else {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsAudioPlaying(true);
            })
            .catch(error => {
              console.error("音频播放失败:", error);
              setIsAudioPlaying(false);
            });
        }
      }
      setIsAudioPlaying(!isAudioPlaying);
    }
  };

  const handleVideoLoad = () => {
    setIsLoading(false);
    setIsPlaying(false);
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error('Video loading error:', e);
    setIsLoading(false);
  };

  const handleAudioLoad = () => {
    setIsAudioLoading(false);
    setIsAudioPlaying(false);
  };

  const handleAudioError = (e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    console.error('Audio loading error:', e);
    setIsAudioLoading(false);
  };

  const mainFeatures = [
    {
      icon: <FaPenNib className="w-8 h-8" />,
      emoji: "✒️",
      title: "Poem Generation",
      description: "Start with a word, an image, or a fleeting thought—let AI weave it into verse, guiding your poetic journey."
    },
    {
      icon: <FaImage className="w-8 h-8" />,
      emoji: "🖼️",
      title: "Image Creation",
      description: "Turn poetry into imagery, where words bloom into stunning visuals, capturing the soul of your expression."
    },
    {
      icon: <FaMicrophone className="w-8 h-8" />,
      emoji: "🎭",
      title: "Poetry Reading",
      description: "Let your verses be heard. AI-powered recitation brings rhythm, emotion, and depth to every line."
    }
  ];
  
  const bottomFeatures = [
    {
      icon: <FaMusic className="w-8 h-8" />,
      emoji: "🎵",
      title: "Music Composition",
      description: "Create melodies that intertwine with your poetry, enhancing its mood, meaning, and resonance."
    },
    {
      icon: <FaVideo className="w-8 h-8" />,
      emoji: "🎬",
      title: "Video Creation",
      description: "Breathe motion into verse—blend visuals and music into poetic films, where every frame tells a story."
    }
  ];

  return (
    <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden">
      {/* Background styling */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-900/20 to-black" />
      <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-purple-900/20 to-transparent" />
      <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-indigo-900/20 to-transparent" />
      
      {/* Decorative elements */}
      <div className="absolute top-1/4 right-0 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/3 left-0 w-72 h-72 bg-indigo-500/10 rounded-full blur-3xl" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div 
          ref={headerRef}
          className="text-center mb-12 sm:mb-16 md:mb-24 opacity-0 translate-y-8 duration-700 ease-out"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
            Live Your Verse Through Art
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto px-4 sm:px-6">
            Discover the creative tools that bring your poetry to life
          </p>
        </div>

        {/* Top row with 3 features */}
        <div 
          ref={featuresRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 lg:gap-12 max-w-lg md:max-w-none mx-auto opacity-0 translate-y-8 duration-700 ease-out mb-12"
        >
          {mainFeatures.map((feature, index) => (
            <div 
              key={index}
              className="group hover-lift relative p-6 sm:p-8 rounded-2xl bg-white/5 backdrop-blur-sm
                       border border-white/10 hover:border-white/20 transition-all duration-300 
                       hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-500/10"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 transition-transform duration-300 group-hover:-translate-y-1">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 
                              flex items-center justify-center text-white shadow-lg shadow-purple-500/20 transition-all duration-300
                              group-hover:shadow-xl group-hover:shadow-purple-500/30 group-hover:scale-105">
                  {feature.icon}
                </div>
              </div>

              <div className="mt-8 sm:mt-10 text-center">
                <h3 className="flex items-center justify-center text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4 gap-2
                             transition-colors duration-300 group-hover:text-purple-300">
                  <span className="text-xl sm:text-2xl transition-transform duration-300 group-hover:scale-110" 
                        role="img" aria-label={feature.title}>
                    {feature.emoji}
                  </span>
                  {feature.title}
                </h3>
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Bottom row with 2 features centered */}
        <div 
          ref={bottomFeaturesRef}
          className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10 lg:gap-12 max-w-lg sm:max-w-2xl mx-auto opacity-0 translate-y-8 duration-700 ease-out mb-20"
          style={{ transitionDelay: '300ms' }}
        >
          {bottomFeatures.map((feature, index) => (
            <div 
              key={index}
              className="group hover-lift relative p-6 sm:p-8 rounded-2xl bg-white/5 backdrop-blur-sm
                       border border-white/10 hover:border-white/20 transition-all duration-300 
                       hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-500/10"
              style={{ transitionDelay: `${index * 100 + 300}ms` }}
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 transition-transform duration-300 group-hover:-translate-y-1">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 
                              flex items-center justify-center text-white shadow-lg shadow-purple-500/20 transition-all duration-300
                              group-hover:shadow-xl group-hover:shadow-purple-500/30 group-hover:scale-105">
                  {feature.icon}
                </div>
              </div>

              <div className="mt-8 sm:mt-10 text-center">
                <h3 className="flex items-center justify-center text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4 gap-2
                             transition-colors duration-300 group-hover:text-purple-300">
                  <span className="text-xl sm:text-2xl transition-transform duration-300 group-hover:scale-110" 
                        role="img" aria-label={feature.title}>
                    {feature.emoji}
                  </span>
                  {feature.title}
                </h3>
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Creative Showcase */}
        <div
          ref={showcaseRef} 
          className="opacity-0 translate-y-8 duration-700 ease-out"
          style={{ transitionDelay: '400ms' }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-2xl p-8 sm:p-10 border border-white/10 backdrop-blur-sm">
              <div className="text-center mb-10">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Unleash Your Creative Flow</h3>
                <p className="text-gray-300 max-w-2xl mx-auto">
                  Parlando offers a seamless creative experience that transforms your poetic vision from words to complete multimedia experiences.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-purple-500/20 p-2 rounded-lg">
                      <FaPenNib className="w-6 h-6 text-purple-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">Whispered Verses, AI-Enriched</h4>
                      <p className="text-sm text-gray-300">
                      Start with a word or an image, and watch poetry unfold. Our AI refines, expands, and harmonizes your creative voice.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-purple-500/20 p-2 rounded-lg">
                      <FaImage className="w-6 h-6 text-purple-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">Poetry in Light and Color</h4>
                      <p className="text-sm text-gray-300">
                      Transform words into breathtaking visuals, where poetry finds its form. Choose a style, let the image sing.
                      </p>
                    </div>
                  </div>

                  {/* Video showcase with new text */}
                  <div className="mt-8 space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-purple-500/20 p-2 rounded-lg">
                        <FaVideo className="w-6 h-6 text-purple-300" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Verses in Motion</h4>
                        <p className="text-sm text-gray-300">
                          Bring poetry to life through cinematic expression. Watch as words flow across the screen, infused with imagery, rhythm, and emotion—every frame, a living poem.
                        </p>
                      </div>
                    </div>
                    
                    {/* Video showcase */}
                    <div className="relative rounded-xl overflow-hidden aspect-video group [&:fullscreen]:bg-black">
                      <video 
                        ref={videoRef}
                        src="https://res.cloudinary.com/dnvmhrx3i/video/upload/v1740881853/parlando/videos/nnbvrcbhgjomequbkywe.mp4"
                        className={`
                          w-full h-full object-cover [&:fullscreen]:object-contain [&:fullscreen]:w-screen [&:fullscreen]:h-screen
                          ${isMobile ? `
                            [&::-webkit-media-controls-panel] {
                              background-image: linear-gradient(transparent, rgba(0, 0, 0, 0.6)) !important;
                            }
                            [&::-webkit-media-controls-timeline] {
                              background-color: rgba(255, 255, 255, 0.2) !important;
                            }
                            [&::-webkit-media-controls-current-time-display],
                            [&::-webkit-media-controls-time-remaining-display] {
                              color: white !important;
                            }
                            [&::-webkit-media-controls-play-button],
                            [&::-webkit-media-controls-fullscreen-button] {
                              filter: brightness(0) invert(1);
                            }
                          ` : ''}
                        `}
                        poster="/images/features/feature-video.jpg"
                        onLoadedData={handleVideoLoad}
                        onError={handleVideoError}
                        playsInline
                        controls={isMobile}
                      />
                      {/* Video Controls Overlay - Only show on desktop */}
                      {!isMobile && (
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 [.group:fullscreen_&]:bg-black/60">
                          {/* Top gradient */}
                          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/60 to-transparent" />
                          
                          {/* Bottom controls bar */}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent">
                            <div className="p-4 space-y-2">
                              {/* Progress bar */}
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-purple-500 transition-all duration-100" 
                                    style={{ width: videoRef.current ? `${(videoRef.current.currentTime / videoRef.current.duration) * 100}%` : '0%' }}
                                  />
                                </div>
                              </div>
                              
                              {/* Controls */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {isLoading ? (
                                    <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    </div>
                                  ) : (
                                    <button 
                                      onClick={togglePlay}
                                      className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                                    >
                                      {isPlaying ? <FaPause className="w-4 h-4" /> : <FaPlay className="w-4 h-4" />}
                                    </button>
                                  )}
                                  <span className="text-sm text-white/80">
                                    {videoRef.current ? 
                                      `${Math.floor(videoRef.current.currentTime / 60)}:${Math.floor(videoRef.current.currentTime % 60).toString().padStart(2, '0')} / 
                                       ${Math.floor(videoRef.current.duration / 60)}:${Math.floor(videoRef.current.duration % 60).toString().padStart(2, '0')}` 
                                      : '0:00 / 0:00'}
                                  </span>
                                </div>
                                
                                <button 
                                  onClick={() => videoRef.current?.requestFullscreen()}
                                  className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0 0l-5-5m-7 11h4m-4 0v4m0 0l5-5m7 5v-4m0 4h-4m0 0l-5-5" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Audio showcase */}
                    <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm mt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <FaMicrophone className="w-6 h-6 text-purple-300" />
                        </div>
                        <div className="flex-1">
                          <h5 className="text-sm font-medium text-white mb-1">Some words are meant to be felt</h5>
                          <div className="flex items-center gap-4">
                            {isMobile ? (
                              <div className="w-full [&::-webkit-media-controls-panel]:!bg-white/10 [&::-webkit-media-controls-panel]:!backdrop-blur-sm">
                                <audio 
                                  src="https://res.cloudinary.com/dnvmhrx3i/video/upload/v1741970467/poetry-reading_bwh68z.mp3"
                                  controls
                                  className={`
                                    w-full h-[36px] rounded-lg
                                    [&::-webkit-media-controls-panel] {
                                      background: linear-gradient(to right, rgba(168, 85, 247, 0.2), rgba(79, 70, 229, 0.2)) !important;
                                      border-radius: 8px !important;
                                    }
                                    [&::-webkit-media-controls-timeline] {
                                      background-color: rgba(255, 255, 255, 0.2) !important;
                                    }
                                    [&::-webkit-media-controls-current-time-display],
                                    [&::-webkit-media-controls-time-remaining-display] {
                                      color: white !important;
                                    }
                                    [&::-webkit-media-controls-play-button] {
                                      filter: brightness(0) invert(1);
                                    }
                                    [&::-webkit-media-controls-timeline]::-webkit-slider-thumb {
                                      background-color: rgb(168, 85, 247) !important;
                                    }
                                  `}
                                  playsInline
                                />
                              </div>
                            ) : (
                              <>
                                <button 
                                  onClick={toggleAudioPlay}
                                  className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-300 hover:bg-purple-500/30 transition-colors"
                                  disabled={isAudioLoading}
                                >
                                  {isAudioLoading ? (
                                    <div className="w-4 h-4 border-2 border-purple-300/30 border-t-purple-300 rounded-full animate-spin" />
                                  ) : isAudioPlaying ? (
                                    <FaPause className="w-4 h-4" />
                                  ) : (
                                    <FaPlay className="w-4 h-4" />
                                  )}
                                </button>
                                <audio 
                                  ref={audioRef}
                                  src="https://res.cloudinary.com/dnvmhrx3i/video/upload/v1741970467/poetry-reading_bwh68z.mp3"
                                  onLoadedData={handleAudioLoad}
                                  onError={handleAudioError}
                                  className="hidden"
                                  playsInline
                                />
                                <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                                  <div className="h-full bg-purple-500 transition-all duration-100" 
                                       style={{ width: audioRef.current ? `${(audioRef.current.currentTime / audioRef.current.duration) * 100}%` : '0%' }} />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right Column - Example Preview and Audio */}
                <div className="space-y-6">
                  <div className="rounded-xl overflow-hidden relative aspect-[4/5] group">
                    <img 
                      src="/images/features/feature-poem.jpg" 
                      alt="Poetry Creation Example"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h4 className="text-lg font-semibold text-white mb-2">Autumn Whispers</h4>
                      <p className="text-sm text-gray-300 mb-4 italic">
                        "Through crimson leaves and golden light,<br/>
                        Autumn whispers ancient dreams..."
                      </p>
                      <a href="#" className="flex items-center text-purple-300 hover:text-purple-200 text-sm font-medium transition-colors">
                        Explore this creation <FaArrowRight className="ml-2" />
                      </a>
                    </div>
                  </div>

                  {/* Echoes of Emotion */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-purple-500/20 p-2 rounded-lg">
                        <FaMusic className="w-6 h-6 text-purple-300" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Echoes of Emotion</h4>
                        <p className="text-sm text-gray-300">
                          Compose music that breathes with your verse, resonating with every rhythm, mood, and meaning. Your poetry, set to sound.
                        </p>
                      </div>
                    </div>

                    {/* The Voice of Poetry */}
                    <div className="flex items-start gap-4">
                      <div className="bg-purple-500/20 p-2 rounded-lg">
                        <FaMicrophone className="w-6 h-6 text-purple-300" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">The Voice of Poetry</h4>
                        <p className="text-sm text-gray-300">
                          Let poetry be heard. A voice, warm or whispered, bold or tender, gives breath to your words. Listen as meaning deepens through sound, turning verse into an intimate performance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-10 text-center">
              <Link to="/dashboard">
                <button className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 text-white 
                                shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 
                                hover:scale-[1.02] transition-all duration-300">
                  Start Creating Now
                </button> 
              </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features; 